import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Save, Plus, X } from 'lucide-react';

export default function MirrorPage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const targetId = userId || user?._id;

  const [mirror, setMirror] = useState({ word_of_the_year: '', key_commitments: [], mirror_document_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newCommitment, setNewCommitment] = useState('');

  useEffect(() => {
    if (!targetId) return;
    api.get(`/legacy/mirror/${targetId}`)
      .then(r => setMirror(r.data))
      .finally(() => setLoading(false));
  }, [targetId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/legacy/mirror/${targetId}`, mirror);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addCommitment = () => {
    if (!newCommitment.trim()) return;
    setMirror(prev => ({ ...prev, key_commitments: [...prev.key_commitments, newCommitment.trim()] }));
    setNewCommitment('');
  };

  const removeCommitment = (idx) => {
    setMirror(prev => ({ ...prev, key_commitments: prev.key_commitments.filter((_, i) => i !== idx) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 fade-in" data-testid="mirror-page">
      <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#E5E1DC', borderTopWidth: '3px', borderTopColor: '#2A7B88' }}>
        <h2 className="text-lg font-bold mb-1" style={{ color: '#1B2A4A' }}>My Mirror</h2>
        <p className="text-sm mb-5" style={{ color: '#6B7280' }}>
          Your annual Mirror commitments and word for the year. This feeds into Mira&rsquo;s understanding of your journey.
        </p>

        {/* Word of the Year */}
        <div className="mb-5">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1B2A4A' }}>Word of the Year</label>
          <input
            data-testid="mirror-word"
            value={mirror.word_of_the_year || ''}
            onChange={e => setMirror(prev => ({ ...prev, word_of_the_year: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#2A7B88]"
            style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
            placeholder="e.g. Discipline, Scale, Focus..."
          />
          <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>The one word that defines your focus for this year</p>
        </div>

        {/* Key Commitments */}
        <div className="mb-5">
          <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1B2A4A' }}>Key Commitments</label>
          <div className="space-y-2 mb-2">
            {mirror.key_commitments?.map((c, idx) => (
              <div key={idx} className="flex items-start gap-2 p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
                <span className="flex-1 text-sm" style={{ color: '#1B2A4A' }}>{c}</span>
                <button onClick={() => removeCommitment(idx)} className="p-0.5 rounded hover:bg-gray-200 flex-shrink-0">
                  <X size={14} style={{ color: '#D4726A' }} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={newCommitment}
              onChange={e => setNewCommitment(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCommitment())}
              className="flex-1 px-3 py-2 rounded-md border text-sm"
              style={{ borderColor: '#E5E1DC' }}
              placeholder="Add a commitment..."
              data-testid="mirror-new-commitment"
            />
            <button onClick={addCommitment} className="px-3 py-2 rounded-md text-white text-sm" style={{ backgroundColor: '#2A7B88' }}
              data-testid="mirror-add-commitment">
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Save Button */}
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: saved ? '#2D8B5E' : '#1B2A4A' }}
          data-testid="mirror-save">
          <Save size={14} />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Mirror'}
        </button>
      </div>
    </div>
  );
}
